<template>
  <div class="race-column">
    <FiltersHeader
      :carFilter="carFilter"
      :disabledClear="disabledClear"
      :fullView="fullView"
      :ordered="filtersForUI.order"
      :refresh="refreshOptions"
      :smartZoom="smartZoom"
      @clearFilters="clearFilters"
      @updateFilters="updateGalleryFilters"
    />
    <filters-gallery
      :columnNumber="column.id"
      :fullView="fullView"
      :raceFilters="filtersForUI"
      @updateFilters="updateGalleryFilters"
    />
    <div
      class="race-content"
      ref="raceContent"
      :style="{ 'max-height': `calc(100vh - ${headerHeight}px)` }"
    >
      <filters-sidebar
        :class="{ 'filters-overlay': showGallery }"
        :filters="filters"
        :selectedFilters="filtersForServer"
        :showFilters="filtersForUI.showAsideFilters"
        @changeAsideFiltersVisibility="changeAsideFiltersVisibility"
        @changeFilters="changeFiltersHandler"
        @updateSettings="updateUserSettings"
      />
      <loader v-if="column.loading && !column.imageList.length" />
      <div v-else-if="showGallery" class="race-gallery">
        <RecycleRaceGallery
          :cropImageView="cropImageView"
          :gridSize="filtersForUI.gridSize"
          :imgList="column.imageList"
          :raceFilters="filtersForUI"
          :selectedFilters="filtersForServer"
          @scrollyend="scrollyend"
          @selectImg="selectImg"
        />
        <v-btn
          v-if="column.totalImagesNew"
          class="btn-new-images"
          color="primary"
          @click="reset"
        >
          +{{ column.totalImagesNew }} new Images
        </v-btn>
      </div>
      <div v-else class="px-4 py-2">Pictures not found</div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { CARS_COUNT, IMAGE_VIEWS } from "@/constants";
import FiltersGallery from "@/components/raceImageComponents/FiltersGallery";
import FiltersHeader from "@/components/raceImageComponents/FiltersHeader";
import FiltersSidebar from "@/components/raceImageComponents/FiltersSidebar";
import Loader from "@/components/Loader";
import RecycleRaceGallery from "../../components/raceImageComponents/RecycleRaceGallery";

export default {
  name: "Race",
  components: {
    FiltersGallery,
    FiltersHeader,
    FiltersSidebar,
    Loader,
    RecycleRaceGallery,
  },
  props: {
    column: {
      type: Object,
      required: true,
    },
    filters: {
      type: Array,
      required: true,
    },
    fullView: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      headerHeight: 0,
    };
  },
  computed: {
    carFilter() {
      const filter = this.filters.find((el) => el.filter_var === CARS_COUNT);
      if (!filter) {
        return null;
      }

      return {
        handler: (v) => this.changeFiltersHandler(v, filter?.filter_var),
        items: filter?.filter_values || [],
        value: this.filtersForServer[filter?.filter_var] || [],
      };
    },
    carNumbersLabel() {
      return `Img: ${this.column?.totalImagesFiltered}`;
    },
    columnId() {
      return this.column?.id || -1;
    },
    cropImageView() {
      return this.smartZoom.value === IMAGE_VIEWS.auto;
    },
    disabledClear() {
      return !Object.values(this.filtersForServer).flat().length;
    },
    filtersForServer() {
      return this.column?.filtersForServer || {};
    },
    filtersForUI() {
      return this.column?.filtersForUI || {};
    },
    raceId() {
      return this.$route.params.raceId;
    },
    refreshOptions() {
      return {
        handler: this.reset,
        label: this.carNumbersLabel,
      };
    },
    showGallery() {
      return this.column.imageList.length;
    },
    smartZoom() {
      return {
        handler: (value) =>
          this.updateFiltersForUI({
            columnId: this.columnId,
            filter: "imageViewType",
            value,
          }),
        items: [
          {
            icon: "$vuetify.icons.picture",
            label: "Original zoom",
            value: IMAGE_VIEWS.regular,
          },
          {
            icon: "$vuetify.icons.zoom-in",
            label: "Smart zoom",
            value: IMAGE_VIEWS.auto,
          },
        ],
        value: this.filtersForUI.imageViewType,
      };
    },
  },
  methods: {
    ...mapActions({
      clearFiltersForServer: "race/clearFiltersForServer",
      updateColumn: "race/updateColumn",
      updateFiltersForServer: "race/updateFiltersForServer",
      updateFiltersForUI: "race/updateFiltersForUI",
    }),
    changeAsideFiltersVisibility() {
      this.updateFiltersForUI({
        columnId: this.columnId,
        filter: "showAsideFilters",
        value: !this.filtersForUI.showAsideFilters,
      });
    },
    getDiffBetweenObjects(o2, o1) {
      return Object.fromEntries(
        Object.entries(o1).filter(([key, value]) => value !== o2[key])
      );
    },
    getImageList(page, direction = "end") {
      this.$emit("updateGallery", {
        columnId: this.columnId,
        direction,
        page,
      });
    },
    updateUserSettings(params) {
      this.$emit("updateSettings", params);
    },
    changeFiltersHandler(filter, filterVar) {
      this.updateFiltersForServer({
        columnId: this.columnId,
        value: {
          ...this.filtersForServer,
          [filterVar]: filter,
        },
      });
      this.reset();
    },
    scrollyend() {
      if (this.column?.imageList?.length < this.column?.totalImagesFiltered) {
        console.log("scrollyend");
        this.getImageList(this.column.page + 1);
      }
    },
    clearFilters() {
      this.clearFiltersForServer({
        columnId: this.columnId,
        raceId: this.raceId,
      });
    },
    selectImg(imgId) {
      this.$emit("selectImg", imgId);
    },
    reset() {
      this.updateColumn({
        columnId: this.columnId,
        imageList: [],
        page: 1,
        totalImagesFiltered: 0,
        totalImagesNew: 0,
      });
      this.getImageList(1);
    },
    updateGalleryFilters(updatedFilter) {
      this.updateFiltersForUI({
        ...updatedFilter,
        columnId: this.columnId,
      });
    },
  },
  watch: {
    filtersForUI: {
      deep: true,
      handler(newVal, prevVal) {
        const updatedFields = this.getDiffBetweenObjects(prevVal, newVal);
        if (updatedFields.gridSize || updatedFields.order) {
          this.reset();
        }
      },
    },
  },
  mounted() {
    this.headerHeight =
      this.$refs?.raceContent?.getBoundingClientRect()?.top || 0;
  },
};
</script>

<style lang="scss" scoped>
.race-column {
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: var(--v-grey-lighten-5);
  overflow: hidden;
}

.race-content {
  position: relative;
  display: flex;
  flex: 1;
}

.race-gallery {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 8px 0 8px 8px;
  flex: 1;
}

.filters-overlay {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 1;
}

.btn-new-images {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  font-weight: 700;
  font-size: 14px;
}
</style>
